import { ACTIVITY_VARIANTS } from '../activityTypes.js';
import { BAD_ITEM_VARIANTS } from '../badItemTypes.js';
import { GAME, GOOD_LEAD_NAMES, GOOD_LEAD_SUBTITLES, ITEM_TYPES, SCORING } from '../config.js';
import { createItemMotion } from './itemMotion.js';
import { clampItemCenter, getItemSafeBounds, resolveItemLayoutKey } from './itemSafeBounds.js';

function resolveItemLayer(stackIndex = 0) {
    const span = GAME.ITEM_LAYER_MAX - GAME.ITEM_LAYER_BASE + 1;
    return GAME.ITEM_LAYER_BASE + (stackIndex % span);
}

function pick(list, random = Math.random) {
    return list[Math.floor(random() * list.length)];
}

function makeId() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return `lp-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function createItem(type, options = {}) {
    const {
        boardSize = { width: 100, height: 100 },
        lifetimeMs = 2300,
        nowMs = Date.now(),
        random = Math.random,
        recentPositions = [],
        stackIndex = 0,
        overrides = {},
    } = options;

    const layoutKey = resolveItemLayoutKey({ type });
    const position = createItemPosition({ boardSize, random, recentPositions, layoutKey });
    const { vx, vy, rotate } = createItemMotion(random);
    const base = clampItemCenter(
        {
            id: makeId(),
            type,
            x: position.x,
            y: position.y,
            vx,
            vy,
            rotate,
            zIndex: resolveItemLayer(stackIndex),
            lifetimeMs,
            createdAt: nowMs,
            expiresAt: nowMs + lifetimeMs,
        },
        boardSize,
        layoutKey,
    );

    if (type === ITEM_TYPES.GOOD_LEAD) {
        return {
            ...base,
            label: overrides.label ?? pick(GOOD_LEAD_NAMES, random),
            subtitle: overrides.subtitle ?? pick(GOOD_LEAD_SUBTITLES, random),
            labelKey: overrides.labelKey,
            subtitleKey: overrides.subtitleKey,
            points: SCORING.GOOD_LEAD,
            icon: overrides.icon ?? '👤',
            fromBackend: Boolean(overrides.fromBackend),
        };
    }

    if (type === ITEM_TYPES.HOT_LEAD) {
        return {
            ...base,
            labelKey: overrides.labelKey ?? 'items.hot',
            label: overrides.label ?? 'Lead caliente',
            subtitle: overrides.subtitle ?? pick(GOOD_LEAD_SUBTITLES, random),
            points: SCORING.HOT_LEAD,
            icon: overrides.icon ?? '🔥',
            fromBackend: Boolean(overrides.fromBackend),
        };
    }

    if (type === ITEM_TYPES.BONUS) {
        return {
            ...base,
            labelKey: 'items.bonusName',
            label: 'Bonus',
            subtitleKey: 'items.bonus',
            subtitle: 'Racha comercial',
            points: SCORING.BONUS,
            icon: '⭐',
        };
    }

    if (type === ITEM_TYPES.ACTIVITY_ITEM) {
        const variant = pick(ACTIVITY_VARIANTS, random);

        return {
            ...base,
            activityType: variant.activityType,
            label: variant.defaultLabel,
            labelKey: variant.labelKey,
            points: SCORING.ACTIVITY_ITEM,
        };
    }

    if (type === ITEM_TYPES.BAD_ITEM) {
        const variant = pick(BAD_ITEM_VARIANTS, random);

        return {
            ...base,
            badItemKey: variant.badItemKey,
            label: variant.defaultLabel,
            labelKey: variant.labelKey,
            subtitle: variant.defaultSubtitle,
            subtitleKey: `${variant.labelKey}.subtitle`,
            points: -SCORING.BAD_ITEM,
            icon: variant.icon,
        };
    }

    const variant = pick(BAD_ITEM_VARIANTS, random);

    return {
        ...base,
        badItemKey: variant.badItemKey,
        label: variant.defaultLabel,
        labelKey: variant.labelKey,
        subtitle: variant.defaultSubtitle,
        subtitleKey: `${variant.labelKey}.subtitle`,
        points: -SCORING.BAD_ITEM,
        icon: variant.icon,
    };
}

export function createItemPosition({
    boardSize,
    random = Math.random,
    recentPositions = [],
    layoutKey = 'activity',
}) {
    const { minX, maxX, minY, maxY } = getItemSafeBounds(boardSize, layoutKey);
    let candidate = null;
    let attempts = 0;

    while (attempts < 16) {
        attempts += 1;
        candidate = {
            x: minX + random() * Math.max(0.1, maxX - minX),
            y: minY + random() * Math.max(0.1, maxY - minY),
        };

        const tooClose = recentPositions.some((pos) => {
            const dx = candidate.x - pos.x;
            const dy = candidate.y - pos.y;
            return Math.sqrt(dx * dx + dy * dy) < 14;
        });

        if (!tooClose) break;
    }

    return candidate || { x: 50, y: 50 };
}

/**
 * Spawn a lead card from CRM enrichment stream data (never random in spawner).
 * @param {object} reviewRow
 * @param {object} [options]
 * @returns {object | null}
 */
export function createBackendLeadItem(reviewRow, options = {}) {
    if (!reviewRow || reviewRow.status === 'failed') {
        return null;
    }

    const data = reviewRow.data || {};
    const source = reviewRow.sourceLead || {};
    const name = String(data.name || source.name || '').trim();
    if (!name) return null;

    const filledPct = Number(data.filledPct);
    const hasQuality = Number.isFinite(filledPct) && filledPct > 0;
    const type = hasQuality && filledPct >= 72 ? ITEM_TYPES.HOT_LEAD : ITEM_TYPES.GOOD_LEAD;
    const subtitle = hasQuality
        ? `${Math.round(filledPct)}%`
        : pick(GOOD_LEAD_SUBTITLES, options.random);

    return createItem(type, {
        ...options,
        overrides: {
            label: name,
            subtitle,
            fromBackend: true,
        },
    });
}
